import React, { useState, useEffect, useRef } from 'react';
import {
  FileText, Calendar, Download, Printer, Check, RefreshCw, Settings,
  TrendingUp, TrendingDown, DollarSign, Users, Building, Wrench, Save
} from 'lucide-react';
import { Card, Button, Input, Select, Modal, Badge, Loading, EmptyState } from '../components/ui';
import { balancesService } from '../services/api';
import { formatMoney } from '../utils/formatters';
import toast from 'react-hot-toast';

const MESES = [
  { value: 1, label: 'ENERO' },
  { value: 2, label: 'FEBRERO' },
  { value: 3, label: 'MARZO' },
  { value: 4, label: 'ABRIL' },
  { value: 5, label: 'MAYO' },
  { value: 6, label: 'JUNIO' },
  { value: 7, label: 'JULIO' },
  { value: 8, label: 'AGOSTO' },
  { value: 9, label: 'SETIEMBRE' },
  { value: 10, label: 'OCTUBRE' },
  { value: 11, label: 'NOVIEMBRE' },
  { value: 12, label: 'DICIEMBRE' }
];

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;

const BalancesPage = () => {
  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState(null);
  const [balancesHistorico, setBalancesHistorico] = useState([]);
  const [selectedMes, setSelectedMes] = useState(currentMonth);
  const [selectedAnio, setSelectedAnio] = useState(currentYear);
  const [showPreview, setShowPreview] = useState(false);
  const [modalConfig, setModalConfig] = useState(false);
  const [config, setConfig] = useState({
    nombre_edificio: 'EDIFICIO',
    direccion: '',
    presidente_nombre: '',
    presidente_cargo: 'Presidente'
  });
  const [submitting, setSubmitting] = useState(false);
  const printRef = useRef();

  useEffect(() => {
    loadConfiguracion();
    loadBalancesHistorico();
  }, []);

  useEffect(() => {
    if (selectedMes && selectedAnio) {
      loadBalance();
    }
  }, [selectedMes, selectedAnio]);

  const loadConfiguracion = async () => {
    try {
      const response = await balancesService.getConfiguracion();
      if (response.data.data) {
        setConfig(response.data.data);
      }
    } catch (error) {
      console.error('Error cargando configuración:', error);
    }
  };

  const loadBalance = async () => {
    setLoading(true);
    try {
      const response = await balancesService.getByMesAnio(selectedMes, selectedAnio);
      setBalance(response.data.data);
      setShowPreview(true);
    } catch (error) {
      toast.error('Error al cargar el balance');
    } finally {
      setLoading(false);
    }
  };

  const loadBalancesHistorico = async () => {
    try {
      const response = await balancesService.getAll({ anio: selectedAnio });
      setBalancesHistorico(response.data.data || []);
    } catch (error) {
      console.error('Error cargando histórico:', error);
    }
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await balancesService.saveConfiguracion(config);
      toast.success('Configuración guardada');
      setModalConfig(false);
      loadBalance();
    } catch (error) {
      toast.error('Error al guardar configuración');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGuardarBalance = async () => {
    if (!balance) return;
    setSubmitting(true);
    try {
      await balancesService.save(balance);
      toast.success('Balance guardado');
      loadBalancesHistorico();
    } catch (error) {
      toast.error('Error al guardar balance');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAprobar = async () => {
    if (!balance?.id) {
      toast.error('Primero debes guardar el balance');
      return;
    }
    try {
      await balancesService.aprobar(balance.id);
      toast.success('Balance aprobado');
      loadBalance();
    } catch (error) {
      toast.error('Error al aprobar balance');
    }
  };

  const handlePrint = () => {
    if (!balance) return;

    const printWindow = window.open('', '_blank');
    const html = generatePrintHTML(balance);
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  const handleDownloadPDF = () => {
    if (!balance) return;

    const printWindow = window.open('', '_blank');
    const html = generatePrintHTML(balance);
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  const generatePrintHTML = (data) => {
    const mesAnterior = data.mes === 1 ? 12 : data.mes - 1;
    const anioMesAnterior = data.mes === 1 ? data.anio - 1 : data.anio;
    const mesAnteriorNombre = MESES.find(m => m.value === mesAnterior)?.label || '';

    const fmt = (n) => n?.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00';

    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Balance ${data.mes_nombre} ${data.anio}</title>
  <style>
    @page { margin: 2cm; size: A4; }
    body { font-family: "Times New Roman", Times, serif; font-size: 12pt; line-height: 1.5; color: #000; max-width: 700px; margin: 0 auto; padding: 20px; }
    .header { text-align: center; margin-bottom: 25px; }
    .header h1 { font-size: 12pt; font-weight: bold; font-style: italic; margin: 0; letter-spacing: 1px; }
    .header h2 { font-size: 14pt; font-weight: bold; margin-top: 20px; }
    .section { margin-bottom: 20px; }
    .section-title { font-weight: bold; text-decoration: underline; margin-bottom: 8px; }
    .row { display: flex; justify-content: space-between; margin: 3px 0; }
    .concepto { flex: 1; }
    .monto { width: 120px; text-align: right; }
    .subtotal { display: flex; justify-content: flex-end; border-top: 1px solid #000; margin-top: 8px; padding-top: 5px; font-weight: bold; }
    .summary { margin-top: 40px; display: flex; justify-content: center; }
    .summary-box { text-align: left; }
    .summary-row { display: flex; justify-content: space-between; margin: 5px 0; min-width: 280px; }
    .summary-row .label { font-weight: bold; }
    .summary-row .value { text-align: right; min-width: 120px; }
    .summary-row.total { border-top: 1px solid #000; padding-top: 8px; margin-top: 8px; }
    .summary-row.total .value { border-bottom: 3px double #000; }
    .signature { margin-top: 80px; text-align: center; }
    .signature-box { display: inline-block; text-align: center; }
    .signature-line { width: 200px; border-top: 1px solid #000; margin: 0 auto; padding-top: 8px; }
    .signature-name { font-style: italic; margin-bottom: 2px; }
  </style>
</head>
<body>
  <div class="header">
    <h1>JUNTA DE PROPIETARIOS ${data.edificio || 'EDIFICIO'}</h1>
    <h2>BALANCE MES DE ${data.mes_nombre} DEL ${data.anio}</h2>
  </div>
  
  <div class="section">
    <div class="row">
      <span class="concepto">-Saldo al 30 de ${mesAnteriorNombre} del ${anioMesAnterior}</span>
      <span class="monto">S/. ${fmt(data.saldo_anterior)}</span>
    </div>
    ${(data.ingresos || []).map(i => `
    <div class="row">
      <span class="concepto">-${i.concepto}</span>
      <span class="monto">${fmt(i.monto)}</span>
    </div>`).join('')}
    <div class="subtotal"><span>S/. ${fmt(data.total_ingresos)}</span></div>
  </div>
  
  ${(data.personal || []).length > 0 ? `
  <div class="section">
    <div class="section-title">PERSONAL</div>
    ${data.personal.map((p, i) => `
    <div class="row">
      <span class="concepto">-${p.concepto}</span>
      <span class="monto">${i === 0 ? 'S/.' : ''} ${fmt(p.monto)}</span>
    </div>`).join('')}
    <div class="subtotal"><span>(S/ ${fmt(data.total_personal)})</span></div>
  </div>` : ''}
  
  ${(data.gastos_fijos || []).length > 0 ? `
  <div class="section">
    <div class="section-title">GASTOS FIJOS</div>
    ${data.gastos_fijos.map((g, i) => `
    <div class="row">
      <span class="concepto">-${g.concepto}</span>
      <span class="monto">${i === 0 ? 'S/' : ''} ${fmt(g.monto)}</span>
    </div>`).join('')}
    <div class="subtotal"><span>(S/ ${fmt(data.total_gastos_fijos)})</span></div>
  </div>` : ''}
  
  ${(data.gastos_variables || []).length > 0 ? `
  <div class="section">
    <div class="section-title">GASTOS VARIABLES</div>
    ${data.gastos_variables.map((g, i) => `
    <div class="row">
      <span class="concepto">-${g.concepto}</span>
      <span class="monto">${i === 0 ? 'S/.' : ''} ${fmt(g.monto)}</span>
    </div>`).join('')}
    <div class="subtotal"><span>(S/ ${fmt(data.total_gastos_variables)})</span></div>
  </div>` : ''}
  
  <div class="summary">
    <div class="summary-box">
      <div class="summary-row">
        <span class="label">INGRESOS</span>
        <span class="value">S/. ${fmt(data.total_ingresos)})</span>
      </div>
      <div class="summary-row">
        <span class="label">EGRESOS</span>
        <span class="value">(S/. ${fmt(data.total_egresos)})</span>
      </div>
      <div class="summary-row total">
        <span class="label">DISPONIBLE</span>
        <span class="value">S/. ${fmt(data.saldo_final)}</span>
      </div>
    </div>
  </div>
  
  <div class="signature">
    <div class="signature-box">
      <div class="signature-line">
        <div class="signature-name">${data.presidente || ''}</div>
        <div>${data.cargo_presidente || 'Presidente'}</div>
      </div>
    </div>
  </div>
</body>
</html>`;
  };

  const getEstadoVariant = (estado) => {
    const variants = { BORRADOR: 'default', PENDIENTE: 'warning', APROBADO: 'success' };
    return variants[estado] || 'default';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Balance Mensual</h1>
          <p className="text-slate-400">Genera el balance mensual resumido del edificio</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => setModalConfig(true)} icon={Settings}>
            Configuración
          </Button>
          <Button variant="secondary" onClick={loadBalance} icon={RefreshCw}>
            Actualizar
          </Button>
        </div>
      </div>

      {/* Selector de período */}
      <Card className="p-5">
        <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
          <Calendar size={18} className="text-blue-400" />
          Seleccionar Período
        </h3>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs text-slate-400 mb-2">Mes</label>
            <Select
              value={selectedMes}
              onChange={(e) => setSelectedMes(parseInt(e.target.value))}
              options={MESES}
            />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-2">Año</label>
            <Select
              value={selectedAnio}
              onChange={(e) => setSelectedAnio(parseInt(e.target.value))}
              options={[
                { value: 2023, label: '2023' },
                { value: 2024, label: '2024' },
                { value: 2025, label: '2025' }
              ]}
            />
          </div>
          <Button onClick={loadBalance} disabled={loading}>
            {loading ? 'Cargando...' : 'Ver Balance'}
          </Button>
        </div>
      </Card>

      {/* Stats del balance */}
      {balance && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-emerald-500/20 text-emerald-400">
                <TrendingUp size={20} />
              </div>
              <div>
                <p className="text-xs text-slate-400">Total Ingresos</p>
                <p className="text-xl font-bold text-emerald-400">{formatMoney(balance.total_ingresos)}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-rose-500/20 text-rose-400">
                <TrendingDown size={20} />
              </div>
              <div>
                <p className="text-xs text-slate-400">Total Egresos</p>
                <p className="text-xl font-bold text-rose-400">{formatMoney(balance.total_egresos)}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-blue-500/20 text-blue-400">
                <DollarSign size={20} />
              </div>
              <div>
                <p className="text-xs text-slate-400">Saldo Disponible</p>
                <p className="text-xl font-bold text-blue-400">{formatMoney(balance.saldo_final)}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-purple-500/20 text-purple-400">
                <FileText size={20} />
              </div>
              <div>
                <p className="text-xs text-slate-400">Estado</p>
                <Badge variant={getEstadoVariant(balance.estado)} className="mt-1">
                  {balance.estado || 'BORRADOR'}
                </Badge>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Detalle de Ingresos */}
      {balance && (
        <Card className="p-5">
          <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
            <TrendingUp size={18} className="text-emerald-400" />
            Detalle de Ingresos
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between text-slate-300 pb-2 border-b border-slate-700">
              <span>-Saldo al 30 de {MESES.find(m => m.value === (balance.mes === 1 ? 12 : balance.mes - 1))?.label} del {balance.mes === 1 ? balance.anio - 1 : balance.anio}</span>
              <span className="font-mono">S/. {balance.saldo_anterior?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</span>
            </div>
            {(balance.ingresos || []).map((item, idx) => (
              <div key={idx} className="flex justify-between text-slate-300">
                <span>-{item.concepto}</span>
                <span className="font-mono">{item.monto?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</span>
              </div>
            ))}
            <div className="flex justify-end pt-2 border-t-2 border-emerald-500">
              <span className="font-bold text-emerald-400 font-mono">S/. {balance.total_ingresos?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</span>
            </div>
          </div>
        </Card>
      )}

      {/* Desglose por categoría */}
      {balance && (
        <div className="grid lg:grid-cols-3 gap-4">
          <Card className="p-4">
            <h4 className="font-medium text-white mb-3 flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Users size={16} className="text-rose-400" />
                Personal
              </span>
              <span className="text-rose-400">{formatMoney(balance.total_personal)}</span>
            </h4>
            <div className="space-y-1.5 text-sm max-h-40 overflow-y-auto">
              {(balance.personal || []).map((item, idx) => (
                <div key={idx} className="flex justify-between text-slate-400">
                  <span className="truncate">{item.concepto}</span>
                  <span>{formatMoney(item.monto)}</span>
                </div>
              ))}
              {(!balance.personal || balance.personal.length === 0) && (
                <p className="text-slate-500 text-xs">Sin gastos de personal</p>
              )}
            </div>
          </Card>

          <Card className="p-4">
            <h4 className="font-medium text-white mb-3 flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Building size={16} className="text-amber-400" />
                Gastos Fijos
              </span>
              <span className="text-amber-400">{formatMoney(balance.total_gastos_fijos)}</span>
            </h4>
            <div className="space-y-1.5 text-sm max-h-40 overflow-y-auto">
              {(balance.gastos_fijos || []).map((item, idx) => (
                <div key={idx} className="flex justify-between text-slate-400">
                  <span className="truncate">{item.concepto}</span>
                  <span>{formatMoney(item.monto)}</span>
                </div>
              ))}
              {(!balance.gastos_fijos || balance.gastos_fijos.length === 0) && (
                <p className="text-slate-500 text-xs">Sin gastos fijos</p>
              )}
            </div>
          </Card>

          <Card className="p-4">
            <h4 className="font-medium text-white mb-3 flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Wrench size={16} className="text-purple-400" />
                Gastos Variables
              </span>
              <span className="text-purple-400">{formatMoney(balance.total_gastos_variables)}</span>
            </h4>
            <div className="space-y-1.5 text-sm max-h-40 overflow-y-auto">
              {(balance.gastos_variables || []).map((item, idx) => (
                <div key={idx} className="flex justify-between text-slate-400">
                  <span className="truncate">{item.concepto}</span>
                  <span>{formatMoney(item.monto)}</span>
                </div>
              ))}
              {(!balance.gastos_variables || balance.gastos_variables.length === 0) && (
                <p className="text-slate-500 text-xs">Sin gastos variables</p>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Acciones */}
      {balance && (
        <Card className="p-4">
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button variant="secondary" onClick={handlePrint} icon={Printer}>
              Imprimir
            </Button>
            <Button variant="secondary" onClick={handleDownloadPDF} icon={Download}>
              Descargar PDF
            </Button>
          </div>
        </Card>
      )}

      {/* Modal Configuración */}
      <Modal
        isOpen={modalConfig}
        onClose={() => setModalConfig(false)}
        title="Configuración del Edificio"
      >
        <form onSubmit={handleSaveConfig} className="space-y-4">
          <Input
            label="Nombre del Edificio"
            value={config.nombre_edificio}
            onChange={(e) => setConfig({ ...config, nombre_edificio: e.target.value })}
            placeholder="Ej: EDIFICIO PORTA"
            required
          />

          <Input
            label="Dirección"
            value={config.direccion}
            onChange={(e) => setConfig({ ...config, direccion: e.target.value })}
            placeholder="Av. Principal 123"
          />

          <Input
            label="Nombre del Presidente"
            value={config.presidente_nombre}
            onChange={(e) => setConfig({ ...config, presidente_nombre: e.target.value })}
            placeholder="Ej: Juan Pérez García"
          />

          <Input
            label="Cargo"
            value={config.presidente_cargo}
            onChange={(e) => setConfig({ ...config, presidente_cargo: e.target.value })}
            placeholder="Presidente"
          />

          <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
            <p className="text-sm text-blue-300">
              Esta información aparecerá en el encabezado y firma del balance mensual.
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setModalConfig(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-12">
          <Loading text="Cargando balance..." />
        </div>
      )}

      {/* Sin balance */}
      {!loading && !balance && (
        <EmptyState
          icon={FileText}
          title="Selecciona un período"
          description="Elige el mes y año para ver el balance correspondiente"
        />
      )}
    </div>
  );
};

export default BalancesPage;
